
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { DashboardPreview } from '../docs/visuals/DashboardPreview';
import { PhoneMockup } from '../HeroAnimation';
import { Button } from '../ui/Button';
import { ArrowRight, CheckCircle2, Layout, Smartphone, Globe, Shield, Code2, Terminal, Server, Lock } from 'lucide-react';
import { motion } from 'framer-motion';

interface SolutionsPageProps {
    onLogin: () => void;
    onNavigate: (page: 'landing' | 'docs' | 'solutions' | 'card-landing' | 'about-landing' | 'legal') => void;
    onNavigateToDocs: (section?: string) => void;
}

const StatItem = ({ value, label }: { value: string, label: string }) => (
    <div className="flex flex-col items-center justify-center p-6 border-b md:border-b-0 md:border-r border-stone-200 dark:border-white/10 last:border-0 md:last:border-r-0">
        <div className="text-3xl font-serif font-bold text-[#0A0B0F] dark:text-white mb-1 tracking-tighter">{value}</div>
        <div className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">{label}</div>
    </div>
);

export const SolutionsPage: React.FC<SolutionsPageProps> = ({ onLogin, onNavigate, onNavigateToDocs }) => {
    return (
        <div className="bg-white dark:bg-[#0A0B0F] min-h-screen font-sans text-[#0A0B0F] dark:text-white overflow-x-hidden">
            <Header onLogin={onLogin} onNavigateToDocs={onNavigateToDocs} onNavigate={onNavigate} />
            
            <main>
                <section className="pt-20 pb-20 md:pt-32 md:pb-40 relative overflow-hidden bg-stone-50 dark:bg-[#0A0B0F]">
                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-stone-300 dark:via-white/20 to-transparent opacity-50"></div>
                    
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-stone-200 dark:border-white/10 bg-white dark:bg-white/5 text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-8 shadow-sm">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                    GhostOS Enterprise 1.0.0 Beta
                                </div>
                                <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl font-bold mb-8 leading-tight tracking-tight text-[#0A0B0F] dark:text-white">
                                    The Operating System for <br className="hidden sm:block" />
                                    <span className="italic text-stone-400 dark:text-stone-500 font-light text-6xl md:text-8xl">Sovereign Business.</span>
                                </h1>
                                <p className="text-base sm:text-lg md:text-xl text-stone-600 dark:text-stone-400 max-w-2xl mx-auto mb-10 leading-relaxed font-light px-4">
                                    A unified financial stack connecting on-chain treasury management, physical retail, and autonomous AI agents. Built for scale, security, and silence.
                                </p>
                                <div className="flex flex-col sm:flex-row justify-center gap-4 px-4">
                                    <Button onClick={onLogin} size="lg" variant="primary" className="shadow-xl shadow-nobel-gold/20 h-16 px-10 text-base rounded-md">
                                        Start Integration <ArrowRight size={18} className="ml-2"/>
                                    </Button>
                                    <Button onClick={() => onNavigateToDocs('introduction')} size="lg" variant="outline" className="h-16 px-10 text-base bg-white !text-black border-transparent hover:bg-stone-100 rounded-md">
                                        Read Documentation
                                    </Button>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                <section className="border-y border-stone-200 dark:border-white/10 bg-white dark:bg-[#0F1014]">
                    <div className="container mx-auto px-6">
                        <div className="grid grid-cols-2 md:grid-cols-4">
                            <StatItem value="$1.2B+" label="Processed Volume" />
                            <StatItem value="< 400ms" label="Settlement Time" />
                            <StatItem value="99.9%" label="Uptime SLA" />
                            <StatItem value="Zero" label="Chargebacks" />
                        </div>
                    </div>
                </section>

                <section className="py-24 md:py-32 bg-white dark:bg-[#0A0B0F] border-b border-stone-100 dark:border-white/5">
                    <div className="container mx-auto px-6">
                        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                            <div className="w-full lg:w-5/12">
                                <div className="p-3 bg-stone-100 dark:bg-white/10 rounded-lg w-fit mb-6 text-stone-900 dark:text-white">
                                    <Layout size={24} />
                                </div>
                                <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 tracking-tight text-[#0A0B0F] dark:text-white">Treasury Command</h2>
                                <p className="text-base sm:text-lg text-stone-600 dark:text-stone-400 mb-8 leading-relaxed">
                                    Consolidate your multi-chain operations into a single pane of glass. Monitor revenue streams, manage liquidity, and generate compliance-ready reports in real-time.
                                </p>
                                <div className="space-y-6">
                                    {[
                                        { title: "Real-time Analytics", desc: "Granular insights into sales performance across all channels." },
                                        { title: "Role-Based Access", desc: "Granular permissions for admins, managers, and auditors." },
                                        { title: "Automated Reconciliation", desc: "Instant matching of on-chain events to invoices." }
                                    ].map((item, i) => (
                                        <div key={i} className="flex gap-4">
                                            <div className="mt-1 shrink-0"><CheckCircle2 size={20} className="text-green-600 dark:text-green-500" /></div>
                                            <div>
                                                <h4 className="font-bold text-stone-900 dark:text-white text-sm">{item.title}</h4>
                                                <p className="text-sm text-stone-500 leading-snug">{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-10">
                                    <Button variant="outline" onClick={() => onNavigateToDocs('dashboard')} className="group rounded-md bg-stone-900 !text-white dark:bg-white dark:!text-black border-transparent">
                                        Explore Dashboard <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </div>
                            </div>
                            <div className="w-full lg:w-7/12">
                                <motion.div 
                                    initial={{ opacity: 0, scale: 0.98 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="relative"
                                >
                                    <div className="absolute -inset-2 bg-gradient-to-br from-stone-200 to-stone-100 dark:from-white/5 dark:to-transparent rounded-2xl blur-xl opacity-40"></div>
                                    <div className="relative rounded-card overflow-hidden border border-stone-200 dark:border-white/10 shadow-2xl bg-white dark:bg-[#0A0B0F]">
                                        <DashboardPreview />
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-24 md:py-32 bg-stone-50 dark:bg-[#0F1014] overflow-hidden border-b border-stone-200 dark:border-white/5">
                    <div className="container mx-auto px-6">
                        <div className="flex flex-col lg:flex-row items-center gap-16 md:gap-24">
                            <div className="w-full lg:w-1/2 flex justify-center lg:justify-end relative order-2 lg:order-1">
                                <div className="absolute inset-0 bg-radial-gradient from-stone-200 to-transparent dark:from-white/5 opacity-50 blur-3xl scale-150"></div>
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8 }}
                                    className="relative z-10"
                                >
                                    <PhoneMockup />
                                </motion.div>
                            </div>
                            <div className="w-full lg:w-1/2 order-1 lg:order-2">
                                <div className="p-3 bg-stone-200 dark:bg-white/10 rounded-lg w-fit mb-6 text-stone-900 dark:text-white">
                                    <Smartphone size={24} />
                                </div>
                                <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 tracking-tight text-[#0A0B0F] dark:text-white">Field Operations</h2>
                                <p className="text-base sm:text-lg text-stone-600 dark:text-stone-400 mb-8 leading-relaxed">
                                    Deploy a secure Point-of-Sale system to any smartphone. Enable your workforce to accept payments, manage inventory, and verify proofs without expensive proprietary hardware.
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                                    <div className="p-6 rounded-md bg-white dark:bg-white/5 border border-stone-200 dark:border-white/5 shadow-sm">
                                        <Globe size={20} className="text-stone-900 dark:text-white mb-4"/>
                                        <h4 className="font-bold text-stone-900 dark:text-white text-sm mb-1">Global Acceptance</h4>
                                        <p className="text-xs text-stone-500 leading-relaxed">Native support for USDC, SOL, and SUI on any network.</p>
                                    </div>
                                    <div className="p-6 rounded-md bg-white dark:bg-white/5 border border-stone-200 dark:border-white/5 shadow-sm">
                                        <Lock size={20} className="text-stone-900 dark:text-white mb-4"/>
                                        <h4 className="font-bold text-stone-900 dark:text-white text-sm mb-1">Biometric Auth</h4>
                                        <p className="text-xs text-stone-500 leading-relaxed">Hardware-level security using FaceID & Secure Enclave.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-24 md:py-32 bg-[#0A0B0F] text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white opacity-5 blur-[120px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="flex flex-col lg:flex-row items-center gap-16">
                            <div className="w-full lg:w-1/2">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-[10px] font-bold uppercase tracking-widest text-gray-300 mb-6">
                                    <Code2 size={12} /> Developer First
                                </div>
                                <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6 tracking-tight">Built for Builders.</h2>
                                <p className="text-base sm:text-lg text-gray-400 mb-8 leading-relaxed max-w-lg">
                                    Integrate GhostPay into your existing stack with our robust SDKs and webhooks. Designed for modularity and extensibility.
                                </p>
                                <div className="space-y-3 font-mono text-[11px] sm:text-sm">
                                    <div className="flex items-center gap-4 p-4 rounded-md bg-white/5 border border-white/10">
                                        <Terminal size={18} className="text-nobel-gold shrink-0" />
                                        <span>npm install @ghostpay/sdk</span>
                                    </div>
                                    <div className="flex items-center gap-4 p-4 rounded-md bg-white/5 border border-white/10">
                                        <Server size={18} className="text-nobel-gold shrink-0" />
                                        <span>POST /payments/create</span>
                                    </div>
                                    <div className="flex items-center gap-4 p-4 rounded-md bg-white/5 border border-white/10">
                                        <Lock size={18} className="text-nobel-gold shrink-0" />
                                        <span>x-api-key: sk_live_...</span>
                                    </div>
                                </div>
                                <div className="mt-10">
                                    <Button variant="secondary" onClick={() => onNavigateToDocs('api-reference')} className="bg-white !text-black hover:bg-stone-200 h-14 rounded-md font-bold px-10">
                                        Read API Docs
                                    </Button>
                                </div>
                            </div>
                            <div className="w-full lg:w-1/2">
                                <div className="rounded-card overflow-hidden border border-white/10 bg-[#0F1014] shadow-2xl">
                                    <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/5">
                                        <div className="w-2 h-2 rounded-full bg-red-500/40 border border-red-500/50"></div>
                                        <div className="w-2 h-2 rounded-full bg-yellow-500/40 border border-yellow-500/50"></div>
                                        <div className="w-2 h-2 rounded-full bg-green-500/40 border border-green-500/50"></div>
                                        <div className="ml-2 text-[10px] font-mono text-gray-500">ghostpay_init.ts</div>
                                    </div>
                                    <div className="p-6 sm:p-8 overflow-x-auto bg-black/40">
<pre className="text-[10px] sm:text-xs md:text-sm font-mono leading-relaxed text-gray-300">
<span className="text-purple-400">import</span> &#123; GhostPay &#125; <span className="text-purple-400">from</span> <span className="text-green-400">'@ghostpay/sdk'</span>;

<span className="text-gray-500">// Initialize Client</span>
<span className="text-blue-400">const</span> ghost = <span className="text-blue-400">new</span> GhostPay(&#123;
  apiKey: process.env.GHOST_KEY
&#125;);

<span className="text-gray-500">// Create Payment Intent</span>
<span className="text-blue-400">const</span> pay = <span className="text-blue-400">await</span> ghost.payments.create(&#123;
  amount: <span className="text-yellow-400">150.00</span>,
  currency: <span className="text-green-400">'USDC'</span>,
  network: <span className="text-green-400">'SOL'</span>,
&#125;);
</pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer onLogin={onLogin} onNavigateToDocs={onNavigateToDocs} onNavigate={onNavigate} />
        </div>
    );
};
